import { useCallback, useEffect, useRef } from "react";
import { Category, ContextPage, EventType } from "../constants";
import { safeReadSessionString, safeWriteSessionString } from "../storage";
import { useTrackingEvent } from "./useTrackingEvent";

const bookingNavKey = (publicationId) =>
  `tracking.detail.bookingNav.v1.${String(publicationId || "")}`;

export const usePublicationTracking = ({
  publicationId,
  establishmentId,
  enabled = true,
  trackView = true,
  trackTimeSpent = true,
}) => {
  const { runtime, trackEvent } = useTrackingEvent();
  const hasTrackedViewRef = useRef(false);
  const startTsRef = useRef(null);
  const finishedRef = useRef(false);

  const markBookingNavigation = useCallback(() => {
    if (!publicationId) return;
    safeWriteSessionString(bookingNavKey(publicationId), "1");
  }, [publicationId]);

  const finalizeTimeSpent = useCallback(
    async ({ reason }) => {
      if (finishedRef.current) return;

      const startTs = startTsRef.current;
      if (!startTs || !establishmentId || !publicationId) {
        finishedRef.current = true;
        return;
      }

      const endTs = Date.now();
      const durationMs = Math.max(0, endTs - startTs);
      const endTimeIso = new Date(endTs).toISOString();

      finishedRef.current = true;

      if (!trackTimeSpent) return;

      await trackEvent(
        {
          schemaVersion: runtime.schemaVersion,
          eventTime: endTimeIso,
          category: Category.MARKETING_EVENT,
          eventType: EventType.TIME_SPENT_ON_PUBLICATION,
          establishmentId,
          publicationId,
          durationMs,
          visitorCountry: runtime.visitorCountry?.countryCode || undefined,
          context: {
            source: runtime.trackingSource,
            page: ContextPage.PUBLICATION_DETAILS,
          },
        },
        { keepalive: reason === "beforeunload" }
      );

      const navigatedToBooking =
        safeReadSessionString(bookingNavKey(publicationId), null) === "1";

      if (durationMs < 10000 && !navigatedToBooking) {
        await trackEvent(
          {
            schemaVersion: runtime.schemaVersion,
            eventTime: endTimeIso,
            category: Category.MARKETING_EVENT,
            eventType: EventType.BOUNCE_EVENT,
            establishmentId,
            publicationId,
            visitorCountry: runtime.visitorCountry?.countryCode || undefined,
            context: {
              source: runtime.trackingSource,
              page: ContextPage.PUBLICATION_DETAILS,
            },
          },
          { keepalive: reason === "beforeunload" }
        );
      }
    },
    [establishmentId, publicationId, runtime, trackEvent, trackTimeSpent]
  );

  useEffect(() => {
    if (!enabled || !trackView) return undefined;
    if (!publicationId || !establishmentId) return undefined;
    if (hasTrackedViewRef.current) return undefined;

    hasTrackedViewRef.current = true;
    startTsRef.current = Date.now();

    safeWriteSessionString(bookingNavKey(publicationId), null);

    trackEvent({
      schemaVersion: runtime.schemaVersion,
      eventTime: new Date().toISOString(),
      category: Category.VISIBILITY_EVENT,
      eventType: EventType.PUBLICATION_VIEW,
      establishmentId,
      publicationId,
      visitorCountry: runtime.visitorCountry?.countryCode || undefined,
      context: { source: runtime.trackingSource, page: ContextPage.PUBLICATION_DETAILS },
    }).catch(() => {});
  }, [enabled, trackView, establishmentId, publicationId, runtime, trackEvent]);

  useEffect(() => {
    if (!hasTrackedViewRef.current || !trackTimeSpent) return undefined;

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        finalizeTimeSpent({ reason: "visibilitychange" }).catch(() => {});
      }
    };

    const onBeforeUnload = () => {
      finalizeTimeSpent({ reason: "beforeunload" }).catch(() => {});
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("beforeunload", onBeforeUnload);
      finalizeTimeSpent({ reason: "unmount" }).catch(() => {});
    };
  }, [finalizeTimeSpent, trackTimeSpent]);

  return { markBookingNavigation, finalizeTimeSpent };
};
